import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition, faAngleDoubleLeft, faAngleDoubleRight, faCheck, faGripLinesVertical, faSitemap } from '@fortawesome/free-solid-svg-icons';
import { IDisplayableItem } from 'src/app/models/interfaces/displayable-item.interface';
import { ItemAccess } from './models/item-access';
import { ReachableElement } from './models/reachable-element';

@Component({
    selector: 'app-item-access',
    templateUrl: './item-access.component.html',
    styleUrls: ['./item-access.component.scss'],
    imports: [CdkDropListGroup, CdkDropList, CdkDrag, FaIconComponent, CdkDragHandle, NgClass, MatTooltipModule, MatButtonModule],
})
export class ItemAccessComponent<T extends IDisplayableItem> {
    public faCheck = faCheck;
    public faAngleDoubleLeft = faAngleDoubleLeft;
    public faAngleDoubleRight = faAngleDoubleRight;
    public faGripLinesVertical = faGripLinesVertical;
    public faSitemap = faSitemap;

    /**
     * Element pour lequel les accès sont gérés.
     */
    @Input()
    public reachableElement: ReachableElement;

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    /**
     * Liste de tous les objets pouvant acceder à l'élément.
     */
    @Input()
    allItems: T[];

    /**
     * Liste de tous les objets disponibles pouvant acceder à l'élément.
     * Il s'agit des objets non sélectionnés.
     */
    @Input()
    availableItems: ItemAccess<T>[];

    /**
     * Liste de tous les objets parents ayant un accès à l'élément.
     */
    @Input()
    inheritedAccess: ItemAccess<T>[];

    /**
     * Titre de la section des objets disponibles pouvant acceder à l'élément.
     */
    @Input()
    availableItemsTitle: string;

    /**
     * Icône à afficher devant les éléments.
     */
    @Input()
    icon: IconDefinition | undefined;

    /**
     * Liste de tous les objets ayant acceder à l'élément.
     * Il s'agit des objets sélectionnés.
     */
    @Input()
    selectedItems: ItemAccess<T>[];

    /**
     * Emis lorsque les access à l'élément sont modifiés
     */
    @Output()
    public accessUpdated: EventEmitter<ItemAccess<T>[]> = new EventEmitter();

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * appelée lorsqu'un objet est déplacé de la liste des objets disponibles vers la liste des objets sélectionnés.
     */
    public dropIntoSelectedItemsList(event: CdkDragDrop<string[]>) {
        if (event.container !== event.previousContainer) {
            console.log('dropIntoSelectedItemsList', event);
            let droppedItem = (event.previousContainer.data as any as ItemAccess<T>[])[event.previousIndex];
            droppedItem.defaultState = false;
            droppedItem.inherited = false;
            this.selectedItems.push(droppedItem);
            this.availableItems.splice(event.previousIndex, 1);
            this.accessUpdated.emit(this.selectedItems);
        }
    }

    /**
     * appelée lorsqu'un objet est déplacé de la liste des objets sélectionnés vers la liste des objets disponibles.
     */
    public dropIntoAvailableItemsList(event: CdkDragDrop<string[]>) {
        if (event.container !== event.previousContainer) {
            console.log('dropIntoAvailableItemsList', event);
            let droppedItem = (event.previousContainer.data as any as ItemAccess<T>[])[event.previousIndex];

            this.moveIntoAvailableItemsList(event.previousIndex, droppedItem);
            this.accessUpdated.emit(this.selectedItems);
        }
    }

    /**
     * Appelée lorsque l'utilisateur veut modifier l'état 'définir comme objet par défaut' d'un objet.
     * @param item objet dont l'état doit être modifié
     */
    public toggleDefaultState(itemAccess: ItemAccess<T>) {
        if (!this.disabled) {
            console.log('toggleDefaultState', itemAccess);

            if (!itemAccess.inherited) {
                itemAccess.defaultState = !itemAccess.defaultState;
            } else {
                // Tentative de modification de l'état par défaut d'un accès hérité --> création d'un nouvel accès non hérité en remplacement de l'accès hérité.
                let newItemAccess = new ItemAccess(itemAccess.item, !itemAccess.defaultState, false);
                const index = this.selectedItems.findIndex((x) => x.item.id === itemAccess.item.id);
                this.selectedItems.splice(index, 1);
                this.selectedItems.push(newItemAccess);
            }

            this.accessUpdated.emit(this.selectedItems);
        }
    }

    /**
     * Déplace tous les éléments dans la liste des éléments disponibles.
     */
    public moveAllItemsInAvailableItemsList() {
        console.log('moveAllItemsInAvailableItemsList');
        [...this.selectedItems].forEach((itemAccess) => {
            const itemAccessIndex = this.selectedItems.findIndex((x) => x.item.id === itemAccess.item.id);
            this.moveIntoAvailableItemsList(itemAccessIndex, itemAccess);
        });

        this.accessUpdated.emit(this.selectedItems);
    }

    /**
     * Déplace tous les éléments dans la liste des éléments sélectionnés.
     */
    public moveAllItemsInSelectedItemsList() {
        console.log('moveAllItemsInSelectedItemsList');
        this.availableItems.forEach((itemAccess) => {
            itemAccess.defaultState = false;
            itemAccess.inherited = false;
            this.selectedItems.push(itemAccess);
        });

        this.availableItems = [];
        this.accessUpdated.emit(this.selectedItems);
    }

    /**
     * Déplace un élément dans la liste des éléments disponibles.
     * @param itemIndex: Index dans la liste source de l'élément à déplacer
     * @param item: Element à déplacer
     */
    moveIntoAvailableItemsList(itemIndex: number, item: ItemAccess<T>) {
        if (!item.inherited) {
            if (this.inheritedAccess.some((x) => x.item.id === item.item.id) === true) {
                // Il existe un héritage pour l'objet dont l'accès a été retiré --> l'accès hérité est ajouté à la liste.
                const inheritedAccess = this.inheritedAccess.find((x) => x.item.id === item.item.id);
                this.selectedItems.push(inheritedAccess.Clone());
            } else {
                item.defaultState = false;
                item.inherited = false;
                this.availableItems.push(item);
            }

            this.selectedItems.splice(itemIndex, 1);
        }
    }
}
